import React, { useState, useEffect, useRef } from "react";
import { createGrammarSubTopic, updateGrammarSubTopic } from "./adminGrammarService";
import ImagePickerModal from "../../media_images/ImagePickerModal";
import RichTextEditor from "../../components/rich_text_editor/RichTextEditor";
import "../../styles/content.css";
import "./grammarForm.css";

const BASE_ROOT = process.env.REACT_APP_BASE_URL_ROOT || "";
const toPublicUrl = (url) => url?.replace(/\/api(?:\/admin)?\/media-images\//, "/api/public/media-images/") ?? url;
const toImgSrc = (url) => {
  if (!url) return "";
  const src = toPublicUrl(url.startsWith("http") ? url : `${BASE_ROOT}${url}`);
  console.log("[GrammarSubTopicForm] img raw:", url, "→ resolved:", src);
  return src;
};

const extractYoutubeId = (url) => {
  if (!url) return null;
  const m = url.match(/(?:v=|youtu\.be\/|embed\/)([-\w]{11})/);
  return m ? m[1] : null;
};

// ── Migrate legacy sections[] → flat HTML ────────────────────────────────────
const sectionsToHtml = (sections) => {
  if (!sections?.length) return "";
  return sections.map((s) => {
    let html = `<h2>${s.heading}</h2>\n${s.content || ""}`;
    if (s.subSections?.length) {
      html += "\n" + s.subSections.map((ss) =>
        `<h3>${ss.heading}</h3>\n${ss.content || ""}`
      ).join("\n");
    }
    return html;
  }).join("\n");
};

// ── Section Images Panel ─────────────────────────────────────────────────────
const SectionImages = ({ images, onChange }) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const dragIdx = useRef(null);

  const handleSelect = (url) => {
    const match = url.match(/\/api(?:\/public)?\/media-images\/(\d+)\/file/);
    const mediaImageId = match ? Number(match[1]) : null;
    if (!mediaImageId) return;
    onChange([...images, { mediaImageId, url, sortOrder: images.length + 1, altText: "" }]);
  };

  const remove = (idx) => onChange(images.filter((_, i) => i !== idx).map((img, i) => ({ ...img, sortOrder: i + 1 })));

  const move = (idx, dir) => {
    const arr = [...images];
    const t = idx + dir;
    if (t < 0 || t >= arr.length) return;
    [arr[idx], arr[t]] = [arr[t], arr[idx]];
    onChange(arr.map((img, i) => ({ ...img, sortOrder: i + 1 })));
  };

  const updateField = (idx, key, val) => {
    const arr = [...images];
    arr[idx] = { ...arr[idx], [key]: val };
    onChange(arr);
  };

  const onDragStart = (idx) => { dragIdx.current = idx; };
  const onDragOver = (e, idx) => {
    e.preventDefault();
    if (dragIdx.current === null || dragIdx.current === idx) return;
    const arr = [...images];
    const dragged = arr.splice(dragIdx.current, 1)[0];
    arr.splice(idx, 0, dragged);
    dragIdx.current = idx;
    onChange(arr.map((img, i) => ({ ...img, sortOrder: i + 1 })));
  };
  const onDragEnd = () => { dragIdx.current = null; };

  return (
    <div className="gf-section-block">
      <div className="gf-section-block-header">
        <span className="gf-label">Section Images</span>
        <button type="button" className="gf-add-btn" onClick={() => setPickerOpen(true)}>+ Add Image</button>
      </div>
      <p className="gf-empty" style={{ marginBottom: 8 }}>Images are interleaved after content blocks by sort order.</p>
      {images.length === 0 && <p className="gf-empty">No images added yet.</p>}
      <div className="gf-images-list">
        {images.map((img, idx) => (
          <div key={idx} className="gf-image-item" draggable
            onDragStart={() => onDragStart(idx)} onDragOver={(e) => onDragOver(e, idx)} onDragEnd={onDragEnd}>
            <span className="gf-drag-handle">⠿</span>
            <img src={toImgSrc(img.url)} alt={img.altText || ""} className="gf-image-thumb" />
            <div className="gf-image-meta">
              <input placeholder="Alt text" value={img.altText} onChange={(e) => updateField(idx, "altText", e.target.value)} className="gf-input gf-input--sm" />
              <div className="gf-image-order">
                <label className="gf-label-inline">After block:</label>
                <input type="number" min={0} value={img.sortOrder} onChange={(e) => updateField(idx, "sortOrder", Number(e.target.value))} className="gf-input gf-input--xs" />
              </div>
            </div>
            <div className="gf-image-actions">
              <button type="button" onClick={() => move(idx, -1)} disabled={idx === 0} className="gf-icon-btn">↑</button>
              <button type="button" onClick={() => move(idx, 1)} disabled={idx === images.length - 1} className="gf-icon-btn">↓</button>
              <button type="button" onClick={() => remove(idx)} className="gf-icon-btn gf-icon-btn--danger">✕</button>
            </div>
          </div>
        ))}
      </div>
      <ImagePickerModal isOpen={pickerOpen} onClose={() => setPickerOpen(false)} onSelect={handleSelect} defaultImageUse="GENERAL" />
    </div>
  );
};

// ── Main Form ────────────────────────────────────────────────────────────────
const GrammarSubTopicForm = ({ subTopic, grammarTopicId, onSaved, onCancel }) => {
  const isEdit = !!subTopic;
  const [form, setForm] = useState({ title: "", youtubeLink: "", sortOrder: "" });
  const [content, setContent] = useState("");
  const [sectionImages, setSectionImages] = useState([]);
  const [coverImage, setCoverImage] = useState(null);
  const [coverPickerOpen, setCoverPickerOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (subTopic) {
      setForm({ title: subTopic.title || "", youtubeLink: subTopic.youtubeLink || "", sortOrder: subTopic.sortOrder ?? "" });
      setCoverImage(subTopic.coverImageUrl ? { mediaImageId: subTopic.coverImageId, url: subTopic.coverImageUrl } : null);
      setSectionImages(subTopic.images?.map((img) => ({ mediaImageId: img.mediaImageId, url: img.url, sortOrder: img.sortOrder, altText: img.altText || "" })) || []);

      if (subTopic.content) {
        setContent(subTopic.content);
      } else if (subTopic.sections?.length) {
        setContent(sectionsToHtml(subTopic.sections));
      } else {
        setContent("");
      }
    }
  }, [subTopic]); // eslint-disable-line

  const handleSubmit = async () => {
    setError("");
    if (!form.title.trim()) { setError("Title is required."); return; }

    const payload = {
      title: form.title,
      grammarTopicId,
      sortOrder: form.sortOrder !== "" ? Number(form.sortOrder) : null,
      youtubeLink: form.youtubeLink || null,
      coverImageId: coverImage?.mediaImageId ?? null,
      content: content || null,
      images: sectionImages.map(({ mediaImageId, sortOrder, altText }) => ({ mediaImageId, sortOrder, altText })),
    };

    setLoading(true);
    const res = isEdit ? await updateGrammarSubTopic(subTopic.id, payload) : await createGrammarSubTopic(payload);
    setLoading(false);
    if (res.status === 200 || res.status === 201) onSaved();
    else setError(res.message || "Operation failed. Please try again.");
  };

  return (
    <div className="gf-page">
      <div className="gf-topbar">
        <button type="button" className="gf-back-btn" onClick={onCancel}>← Back</button>
        <h2 className="gf-page-title">{isEdit ? "Edit" : "Create"} Sub-Topic</h2>
        <div className="gf-topbar-actions">
          <button type="button" onClick={onCancel} className="gf-btn gf-btn--secondary" disabled={loading}>Cancel</button>
          <button type="button" onClick={handleSubmit} className="gf-btn gf-btn--primary" disabled={loading}>
            {loading ? "Saving…" : isEdit ? "Update" : "Create"}
          </button>
        </div>
      </div>

      {error && <div className="gf-error-banner">{error}</div>}

      <div className="gf-body">
        {/* LEFT — content */}
        <div className="gf-col gf-col--left">
          <div className="gf-card">
            <div className="gf-row">
              <div className="gf-field gf-field--grow">
                <label className="gf-label">Title <span className="gf-required">*</span></label>
                <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Present Simple" className="gf-input" />
              </div>
              <div className="gf-field gf-field--xs">
                <label className="gf-label">Sort</label>
                <input type="number" value={form.sortOrder} onChange={(e) => setForm({ ...form, sortOrder: e.target.value })} placeholder="1" className="gf-input" />
              </div>
            </div>
          </div>

          <div className="gf-card">
            <label className="gf-label" style={{ display: "block", marginBottom: 8 }}>Content</label>
            <RichTextEditor
              value={content}
              onChange={setContent}
              placeholder="Type or paste content here…"
            />
          </div>
        </div>

        {/* RIGHT — meta */}
        <div className="gf-col gf-col--right">
          <div className="gf-card">
            <div className="gf-field">
              <label className="gf-label">Cover Image</label>
              {coverImage ? (
                <div className="gf-cover-preview">
                  <img src={toImgSrc(coverImage.url)} alt="cover" className="gf-cover-thumb" />
                  <button type="button" className="gf-icon-btn gf-icon-btn--danger" onClick={() => setCoverImage(null)}>✕ Remove</button>
                </div>
              ) : (
                <button type="button" className="gf-add-btn" onClick={() => setCoverPickerOpen(true)}>🖼 Pick Cover Image</button>
              )}
              {coverImage && (
                <button type="button" className="gf-text-btn" style={{ marginTop: 4 }} onClick={() => setCoverPickerOpen(true)}>Change image</button>
              )}
            </div>
          </div>

          <div className="gf-card">
            <div className="gf-field">
              <label className="gf-label">YouTube / Vimeo URL</label>
              <div style={{ display: "flex", gap: 6 }}>
                <input value={form.youtubeLink} onChange={(e) => setForm({ ...form, youtubeLink: e.target.value })}
                  placeholder="https://youtube.com/watch?v=…" className="gf-input" style={{ flex: 1 }} />
                {form.youtubeLink && (
                  <button type="button" onClick={() => setForm({ ...form, youtubeLink: "" })} className="gf-icon-btn gf-icon-btn--danger">✕</button>
                )}
              </div>
              {extractYoutubeId(form.youtubeLink) && (
                <img src={`https://img.youtube.com/vi/${extractYoutubeId(form.youtubeLink)}/hqdefault.jpg`}
                  alt="thumbnail" className="gf-yt-thumb" onError={(e) => { e.target.style.display = "none"; }} />
              )}
            </div>
          </div>

          <div className="gf-card">
            <SectionImages images={sectionImages} onChange={setSectionImages} />
          </div>
        </div>
      </div>

      <ImagePickerModal
        isOpen={coverPickerOpen}
        onClose={() => setCoverPickerOpen(false)}
        onSelect={(url) => {
          const match = url.match(/\/api(?:\/public)?\/media-images\/(\d+)\/file/);
          const mediaImageId = match ? Number(match[1]) : null;
          if (mediaImageId) setCoverImage({ mediaImageId, url });
          setCoverPickerOpen(false);
        }}
        defaultImageUse="GENERAL"
      />
    </div>
  );
};

export default GrammarSubTopicForm;
